import { api } from './api';
import { googleMapsService } from './googleMaps.service';

export const getCaseById = async (caseId) => {
  return api.get(`/api/cases/${caseId}`);
};

export const addCaseNote = async (caseId, note) => {
  // note: { note_type, note_text, is_internal }
  return api.post(`/api/cases/${caseId}/notes`, note);
};

export const getCaseNotes = async (caseId) => {
  return api.get(`/api/cases/${caseId}/notes`);
};

export const getCustomerPolicies = async (customerId) => {
  return api.get(`/api/customers/${customerId}/policies`);
};

export const createService = async (serviceData) => {
  return api.post('/api/services/', serviceData);
};

export const getServiceById = async (serviceId) => {
  return api.get(`/api/services/${serviceId}`);
};

export const getServicePhotos = async (serviceId) => {
  return api.get(`/api/services/${serviceId}/photos`);
};

export const getVendorById = async (vendorId) => {
  return api.get(`/api/vendors/${vendorId}`);
};

// New API calls for service types and subtypes
export const getSchemeServiceTypes = async (schemeId) => {
  return api.get(`/api/cases/schemes/${schemeId}/service-types/?is_active=true`);
};

export const getServiceSubtypes = async (serviceTypeId) => {
  return api.get(`/api/cases/service-types/${serviceTypeId}/subtypes/?is_active=true`);
};

export const getDriverDetailsById = async (driverId) => {
  return api.get(`/api/driver/${driverId}`);
};

// Update case status
export const updateCaseStatus = async (caseId, status, notes = null) => {
  const updateData = {
    status: status
  };

  if (notes) {
    updateData.notes = notes;
  }

  return api.put(`/api/cases/${caseId}`, updateData);
};

// Update service comprehensively
export const updateServiceComprehensive = async (serviceId, updateData) => {
  return api.put(`/api/services/${serviceId}`, updateData);
};

// Cancel service
export const cancelService = async (serviceId, cancelData) => {
  // cancelData: { cancelled_by, on_behalf_of, cancellation_reason, notes, reason_code }
  // reason_code is required by the backend when on_behalf_of === 'vendor' (vendor walkaway).
  return api.post(`/api/services/${serviceId}/cancel`, cancelData);
};

// Clear a customer OTP escalation or override the current OTP stage (Agent/Manager only).
// action: 'clear' | 'override'; reason is mandatory (min 5 chars) and audited server-side.
export const resolveOtpEscalation = async (serviceId, action, reason) => {
  return api.post(`/api/admin/services/${serviceId}/otp-escalation`, { action, reason });
};

// Send location sharing message via WhatsApp
export const sendLocationSharingMessage = async (requestData) => {
  return api.post('/api/services/send-location-sharing', requestData);
};

// Helper function to extract lat/long from location update note
export const extractCoordinatesFromNote = (noteText) => {
  // Expected format: "Customer Location Shared: Latitude: {lat}, Longitude: {long}"
  const latMatch = noteText.match(/Latitude:\s*(-?\d+\.?\d*)/);
  const longMatch = noteText.match(/Longitude:\s*(-?\d+\.?\d*)/);

  if (latMatch && longMatch) {
    return {
      latitude: parseFloat(latMatch[1]),
      longitude: parseFloat(longMatch[1])
    };
  }

  return null;
};

// Reverse geocoding using Google Maps API
export const reverseGeocode = async (latitude, longitude) => {
  try {
    // Ensure Google Maps is loaded
    if (!window.google || !window.google.maps) {
      await googleMapsService.initializeGoogleMaps();
    }

    // Get API key from the service
    const apiKey = await googleMapsService.getApiKey();

    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`
    );

    if (!response.ok) {
      throw new Error('Geocoding request failed');
    }

    const data = await response.json();

    if (data.status !== 'OK' || !data.results || data.results.length === 0) {
      throw new Error('No geocoding results found');
    }

    const result = data.results[0];
    const addressComponents = result.address_components;

    // Extract address components
    let address = result.formatted_address;
    let city = '';
    let state = '';
    let pincode = '';

    addressComponents.forEach(component => {
      const types = component.types;

      if (types.includes('locality')) {
        city = component.long_name;
      } else if (types.includes('administrative_area_level_1')) {
        state = component.long_name;
      } else if (types.includes('postal_code')) {
        pincode = component.long_name;
      }
    });

    return {
      address,
      city,
      state,
      pincode,
      latitude,
      longitude
    };

  } catch (error) {
    console.error('Reverse geocoding error:', error);
    throw new Error('Failed to get address from coordinates');
  }
};

// Get dealers list for drop location selection
export const getDealers = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();

    // Add optional parameters
    if (params.search) queryParams.append('search', params.search);
    if (params.dealer_type) queryParams.append('dealer_type', params.dealer_type);
    if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
    if (params.brand) queryParams.append('brand', params.brand);
    if (params.service) queryParams.append('service', params.service);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);

    // Add location-based parameters for radius search
    if (params.latitude) queryParams.append('latitude', params.latitude);
    if (params.longitude) queryParams.append('longitude', params.longitude);
    if (params.radius) queryParams.append('radius_km', params.radius);

    const response = await api.get(`/api/admin/dealers?${queryParams.toString()}`);
    return response;
  } catch (error) {
    console.error('Error fetching dealers:', error);
    throw error;
  }
};

// Get customer location from the new customer_location table
export const getCustomerLocation = async (caseId, mobileNumber = null) => {
  try {
    const params = new URLSearchParams();
    if (caseId) {
      params.append('case_id', caseId);
    }
    if (mobileNumber) {
      params.append('mobile_number', mobileNumber);
    }

    const response = await api.get(`/api/cases/customer-location?${params.toString()}`);

    if (response.found && response.latest_location) {
      const location = response.latest_location;

      // If address is not available in the database, try reverse geocoding
      let addressData = location.address;
      if (!addressData || Object.keys(addressData).length === 0) {
        try {
          addressData = await reverseGeocode(location.latitude, location.longitude);
        } catch (geocodeError) {
          console.warn('Reverse geocoding failed, using coordinates only:', geocodeError);
          addressData = {
            latitude: location.latitude,
            longitude: location.longitude
          };
        }
      }

      return {
        found: true,
        coordinates: {
          latitude: location.latitude,
          longitude: location.longitude
        },
        addressData,
        locationId: location.id,
        createdAt: location.created_at,
        accuracy: location.accuracy,
        totalRecords: response.total_records,
        caseInfo: response.case_info
      };
    }

    return { found: false, message: response.message };
  } catch (error) {
    console.error('Error getting customer location:', error);
    return { found: false, error: error.message };
  }
};

// Legacy function for backward compatibility - now uses the new API
export const checkForLocationUpdate = async (caseId) => {
  return await getCustomerLocation(caseId);
};

// Case Files API endpoints
export const uploadCaseFile = async (caseId, file, fileType, description = null) => {
  const formData = new FormData();
  formData.append('file', file);

  // Build query parameters
  const params = new URLSearchParams();
  params.append('file_type', fileType);
  if (description) {
    params.append('description', description);
  }

  return api.post(`/api/cases/${caseId}/files?${params.toString()}`, formData);
};

export const getCaseFiles = async (caseId, fileType = null) => {
  const params = {};
  if (fileType) {
    params.file_type = fileType;
  }

  return api.get(`/api/cases/${caseId}/files`, { params });
};

export const deleteCaseFile = async (caseId, fileId) => {
  return api.delete(`/api/cases/${caseId}/files/${fileId}`);
};

export const getFileTypes = async () => {
  return api.get('/api/cases/file-types');
};

// Helper function to download file from URL
export const downloadFile = async (url, fileName) => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Download failed');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Error downloading file:', error);
    throw error;
  }
}; 